import React, {MouseEventHandler, useState} from 'react';
import {ControlButton, TscButtonType, Glyphs, TscButtonGlyphPlacement, TscStyles, TscThemeName} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import config from '../../service/config';
import {IComment} from '../../model/commentModel';
import UserMessaging from '../../util/UserMessaging';
import conversationApi from '../../service/conversationApi';
import styled from 'styled-components';
import {SetupWorkerState} from '../../model/workerState';
import Constants from '../../constants/Constants';
import {updateCommentsState, commentsStore} from '../../context/commentsProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {CommentFooterWrapper} from '../util/StyledElements';
import {disableConversationStore} from '../../context/disableConversationProvider';
import {useTscTheme} from '../../context/themeProvider';
import logging from '../../service/logging';
import {updateSelfIdentifiedUserProviderState} from '../../context/selfIdentifiedUserProvider';
import CssUtil from '../../util/CssUtil';

const backgroundOpacity15 = 0.15;
export const LikeButtonWrapper = styled.div<{isReply: boolean; userHasLikedComment: boolean}>`
   align-self: end;
   margin-left: auto;

   svg {
      height: 14px;
      width: 14px;
   }

   button {
      padding: 0.375rem;
      letter-spacing: 0;
      gap: 0.375rem;
      background-color: ${props => props.userHasLikedComment ? props.theme ===
         TscThemeName.dusk ? TscStyles.color.ui.dusk.dark : CssUtil.toRgba(TscStyles.color.action.primary, backgroundOpacity15) : 'transparent'};
      color: ${props => props.userHasLikedComment ? TscStyles.color.action.primary : props.theme ===
         TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
      fill: ${props => props.userHasLikedComment ? TscStyles.color.action.primary : props.theme ===
         TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};

      :hover {
         color: ${props => props.userHasLikedComment ? TscStyles.color.text.light : props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
         fill: ${props => props.userHasLikedComment ? TscStyles.color.text.light : props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
      }
      span{
         filter: ${props => (props.userHasLikedComment && props.theme === TscThemeName.dusk) ? 'brightness(150%)' : 'none'};
      }
   }
`;

const backgroundOpacity60 = 0.6;
export const LikeListWrapper = styled.div<{isReply: boolean}>`
   position: absolute;
   right: -0.25rem;
   margin-top: 4px;
   background-color: ${props => props.theme === TscThemeName.dusk ? CssUtil.toRgba(TscStyles.color.ui.dusk.medium, backgroundOpacity60) : CssUtil.toRgba(TscStyles.color.ui.dawn.lightMedium, backgroundOpacity60)};
   backdrop-filter: blur(12px);
   padding: 0.25rem 0 0.25rem 0.75rem;
   border-radius: ${TscStyles.border.radius.md};
   z-index: 1;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.dark};
   max-width: 15rem;
   width: max-content;
   box-shadow: ${props => props.theme === TscThemeName.dusk ? '0 2px 12px rgba(0, 0, 0, 0.4), 0 .5px 2px rgba(255, 255, 255, 0.2) inset}' : '0 2px 12px rgba(0, 0, 0, 0.2), 0 .5px 2px rgba(255, 255, 255, 0.9) inset}'};

   :before {
      content: '';
      position: absolute;
      width: 100%;
      height: 4px;
      top: -4px;
      right: 0;
   }
`;

const LikeList = styled.div`
   display: grid;
   max-height: 19.75rem;
   overflow-y: auto;
   line-height: 1.4rem;

   span {
      margin-right: 0.75rem;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
   }
`;

const ExpandRepliesButton = styled.div<{expandReplies: boolean}>`
   button {
      letter-spacing: 0;
      gap: 0.25rem;

      span svg:first-child {
         transition: transform 0.3s ease;
         transform: rotate(${props => props.expandReplies ? '180' : '0'}deg);
      }
   }
`;

const StyledReplyButton = styled(ControlButton)`
   svg {
      width: 14px;
      height: 14px;
   }
`;

const StyledEditButton = styled(ControlButton)`
   svg {
      width: 14px;
      height: 14px;
   }
`;

export const NonEditingCommentFooterBase: React.FC<INonEditingCommentFooterProps & IStateMappedProps> = ({comment, isReply, userHasLikedComment, comments, isCommentingDisabled, commentRepliesLength, setExpandReplies, expandReplies, handleExpandReplies}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [loadingState, setLoadingState] = SetupWorkerState();
   const [showListOfLikes, setShowListOfLikes] = useState(false);

   const onClickExpandReplies: MouseEventHandler = event => {
      event.stopPropagation();
      setExpandReplies(!expandReplies);
   };

   const onReplyClick = () => {
      handleExpandReplies();

      window.setTimeout(() => {
         document.getElementById(`${comment.id}-reply-input`)?.focus();
      }, Constants.getDomElementTimeout);
   };

   const onLikeClick = async (e: React.MouseEvent) => {
      e.stopPropagation();

      if (config.user.TechSmithId === Constants.nullTechSmithId) {
         updateSelfIdentifiedUserProviderState({showSelfIdentifyModal: true});
         return;
      }

      setLoadingState({isWorking: true, hasError: false});

      try {
         const updatedComment = userHasLikedComment ? await conversationApi.unlikeComment(comment, config.user.TechSmithId) : await conversationApi.likeComment(comment, config.user.TechSmithId);
         const commentsCopy = Object.assign([], comments);

         if (isReply) {
            const parentComment = commentsCopy.find((c: IComment) => c.id === updatedComment.parentCommentId);
            parentComment.replies.splice(parentComment.replies.findIndex((r: IComment) => r.id === updatedComment.id), 1, updatedComment);
            commentsCopy.splice(commentsCopy.findIndex((oldComment: IComment) => oldComment.id === parentComment.id), 1, parentComment);
         } else {
            commentsCopy.splice(commentsCopy.findIndex((oldComment: IComment) => oldComment.id === comment.id), 1, updatedComment);
         }

         updateCommentsState({comments: commentsCopy});
         setLoadingState({isWorking: false, hasError: false});
      } catch (err) {
         logging.error('Failed to like comment', err);
         UserMessaging.warn(t('conversations.failedSaveComment'));
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   const onEditCommentClick = () => {
      document.getElementById(`comment-editing-alert-${comment.id}`).style.display = 'block';
      const commentsCopy = Object.assign([] as IComment[], comments);

      if (isReply) {
         updateCommentsState({
            comments: commentsCopy.map(c => {
               if (c.id !== comment.parentCommentId) {
                  return c;
               }
               return Object.assign({}, c, {replies: c.replies.map((r: IComment) => r.id === comment.id ? Object.assign({}, r, {isEditing: true}) : r)}) as IComment;
            })
         });
      } else {
         updateCommentsState({
            comments: commentsCopy.map(c => {
               if (c.id === comment.id) {
                  return Object.assign({}, c, {isEditing: true}) as IComment;
               }
               return c;
            })
         });
      }

      setTimeout(() => {
         document.getElementById(`comment-editing-alert-${comment.id}`).style.display = 'none';
      }, 1000);
   };

   const expandThreadText = ` ${commentRepliesLength} ${commentRepliesLength > 1 ? t('conversations.commentReplyThread.replies') : t('conversations.commentReplyThread.reply')}`;
   const showEditButton = ((config.watchConfig.isOwnerAdminOrTeamEditor) && !comment.isDeleted) || comment.authorId === config.user.TechSmithId;

   return (
      <CommentFooterWrapper
         isReply={isReply}
         isEditing={false}
         data-test-id="non-editing-comment-footer"
      >

         {!isReply && (
            <>
               {!!commentRepliesLength && (
                  <ExpandRepliesButton expandReplies={expandReplies}>
                     <ControlButton
                        buttonType={TscButtonType.tertiary}
                        onClick={onClickExpandReplies}
                        label={expandThreadText}
                        glyph={<Glyphs.ArrowDown16x16 />}
                        glyphPlacement={TscButtonGlyphPlacement.left}
                        testId="expand-comment-replies-button"
                        aria={{'aria-expanded': expandReplies}}
                     />
                  </ExpandRepliesButton>
               )}
               <StyledReplyButton
                  onClick={onReplyClick}
                  label={t('conversations.reply')}
                  buttonType={TscButtonType.tertiary}
                  disabled={isCommentingDisabled}
                  testId="comment-reply-btn"
                  glyph={<Glyphs.ArrowReply16x16 />}
               />
            </>
         )}
         {showEditButton && (
            <StyledEditButton
               onClick={onEditCommentClick}
               label={t('conversations.edit')}
               buttonType={TscButtonType.tertiary}
               disabled={isCommentingDisabled}
               testId="comment-edit-btn"
               glyph={<Glyphs.Edit16x16 />}
            />
         )}
         {!comment.isDeleted && (
            <LikeButtonWrapper
               theme={theme}
               isReply={isReply}
               userHasLikedComment={userHasLikedComment}
               onMouseLeave={() => setShowListOfLikes(false)}
               onMouseEnter={() => setShowListOfLikes(true)}
            >
               <ControlButton
                  onClick={onLikeClick}
                  disabled={loadingState.isWorking || comment.isDeleted || isCommentingDisabled}
                  aria={{'aria-pressed': userHasLikedComment, 'aria-label': t('conversations.likeCommentLabel', {count: comment.likes.length})}}
                  buttonType={userHasLikedComment ? TscButtonType.primary : TscButtonType.tertiary}
                  label={comment.likes.length > 0 ? comment.likes.length.toString() : t('conversations.like')}
                  title={comment.likes.length === 0 ? t('conversations.likeCommentTitle') : ''}
                  glyph={<Glyphs.ThumbsUp16x16 />}
                  glyphPlacement={TscButtonGlyphPlacement.left}
                  testId="comment-like-btn"
               />
               {showListOfLikes && !!comment.likes.length && (
                  <>
                     <LikeListWrapper
                        isReply={isReply}
                        theme={theme}
                     >
                        <LikeList data-test-id="like-list">
                           {comment.likes.map(like => (
                              <span key={like.UserId}>
                                 {like.DisplayName}
                              </span>
                           ))}
                        </LikeList>
                     </LikeListWrapper>
                  </>
               )}
            </LikeButtonWrapper>
         )}
      </CommentFooterWrapper>
   );
};

export interface INonEditingCommentFooterProps {
   comment: IComment;
   isReply: boolean;
   userHasLikedComment: boolean;
   commentRepliesLength: number;
   expandReplies: boolean;
   setExpandReplies(expandReplies: boolean): void;
   handleExpandReplies(): void;
}

export interface IStateMappedProps {
   comments: IComment[];
   isCommentingDisabled: boolean;
}

const mapStatesToProps = (
   {comment, isReply, userHasLikedComment, commentRepliesLength, handleExpandReplies, expandReplies, setExpandReplies}: INonEditingCommentFooterProps,
   {comments}: Partial<IStateMappedProps>,
   {isCommentingDisabled}: Partial<IStateMappedProps>
): INonEditingCommentFooterProps & IStateMappedProps => ({comment, isReply, userHasLikedComment, comments, commentRepliesLength, expandReplies, isCommentingDisabled, handleExpandReplies, setExpandReplies});

export default withMemoizedContexts(mapStatesToProps, commentsStore, disableConversationStore)(NonEditingCommentFooterBase);
